// ── Resources & Summary Section Editor ─────────────────────────────────────────
// Top: Team members (card grid, 3 per row) with avatar, name, role, description
import { BRAND_NAME } from '../../lib/brandConfig';
// Bottom: Key metrics (gold numbers) + summary paragraph

import { Plus, Trash2, Users, BarChart3 } from 'lucide-react';
import ImageUploader from './ImageUploader';
import { uid } from '../../lib/uid';


export default function ResourcesSummarySection({ data, onChange }) {
  const rs = data || { team: [], key_metrics: [], summary: '' };

  const update = (key, value) => {
    onChange({ ...rs, [key]: value });
  };

  // ── Team management ────────────────────────────────────────────────────
  const addTeamMember = () => {
    const team = [...(rs.team || [])];
    team.push({ id: uid(), name: '', role: '', description: '', avatar_url: '' });
    update('team', team);
  };

  const removeTeamMember = (idx) => {
    const team = [...(rs.team || [])];
    team.splice(idx, 1);
    update('team', team);
  };

  const updateTeamMember = (idx, field, value) => {
    const team = [...(rs.team || [])];
    team[idx] = { ...team[idx], [field]: value };
    update('team', team);
  };

  // ── Key metrics management ─────────────────────────────────────────────
  const addMetric = () => {
    const metrics = [...(rs.key_metrics || [])];
    metrics.push({ id: uid(), label: '', value: '' });
    update('key_metrics', metrics);
  };

  const removeMetric = (idx) => {
    const metrics = [...(rs.key_metrics || [])];
    metrics.splice(idx, 1);
    update('key_metrics', metrics);
  };

  const updateMetric = (idx, field, value) => {
    const metrics = [...(rs.key_metrics || [])];
    metrics[idx] = { ...metrics[idx], [field]: value };
    update('key_metrics', metrics);
  };

  return (
    <div className="space-y-5">
      {/* Team section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#c9a84c]" />
            <label className="text-xs font-semibold text-[#1a1a2e] uppercase tracking-wider">
              Team Members
            </label>
          </div>
          <button
            type="button"
            onClick={addTeamMember}
            className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#b8973e] font-medium"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>

        {(rs.team || []).length === 0 ? (
          <div className="text-center py-6 bg-[#f8f7f4] border border-[#e5e4df] rounded-lg">
            <Users className="w-8 h-8 mx-auto text-[#c9a84c]/30 mb-2" />
            <p className="text-xs text-[#9ca3af]">No team members yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(rs.team || []).map((member, idx) => (
              <div key={member.id} className="p-3 bg-white border border-[#e5e4df] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-[#6b7280]">#{idx + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeTeamMember(idx)}
                    className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Avatar */}
                <div className="mb-2">
                  <ImageUploader
                    value={member.avatar_url}
                    sourceType={member.avatar_url ? 'local' : 'placeholder'}
                    onChange={({ url }) => updateTeamMember(idx, 'avatar_url', url)}
                    onRemove={() => updateTeamMember(idx, 'avatar_url', '')}
                    label="Avatar"
                    aspectRatio="1:1"
                    height="80px"
                    compact
                  />
                </div>

                <input
                  type="text"
                  value={member.name || ''}
                  onChange={(e) => updateTeamMember(idx, 'name', e.target.value)}
                  placeholder="Name"
                  className="w-full px-2 py-1.5 text-sm border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white font-semibold mb-1.5"
                />
                <input
                  type="text"
                  value={member.role || ''}
                  onChange={(e) => updateTeamMember(idx, 'role', e.target.value)}
                  placeholder="Role (e.g. Director)"
                  className="w-full px-2 py-1.5 text-xs border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white mb-1.5"
                />
                <textarea
                  value={member.description || ''}
                  onChange={(e) => updateTeamMember(idx, 'description', e.target.value)}
                  placeholder="Brief description..."
                  rows={2}
                  className="w-full px-2 py-1.5 text-xs border border-[#e5e4df] rounded focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Key Metrics section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#c9a84c]" />
            <label className="text-xs font-semibold text-[#1a1a2e] uppercase tracking-wider">
              Key Metrics
            </label>
          </div>
          <button
            type="button"
            onClick={addMetric}
            className="flex items-center gap-1 text-xs text-[#c9a84c] hover:text-[#b8973e] font-medium"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>

        {(rs.key_metrics || []).length === 0 ? (
          <div className="text-center py-4 bg-[#f8f7f4] border border-[#e5e4df] rounded-lg">
            <p className="text-xs text-[#9ca3af]">No key metrics yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(rs.key_metrics || []).map((metric, idx) => (
              <div key={metric.id} className="p-3 bg-white border border-[#e5e4df] rounded-lg text-center">
                <button
                  type="button"
                  onClick={() => removeMetric(idx)}
                  className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-100 text-red-500 hover:bg-red-200 transition-colors"
                  style={{ position: 'relative', float: 'right', marginTop: '-4px', marginRight: '-4px' }}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
                <input
                  type="text"
                  value={metric.value || ''}
                  onChange={(e) => updateMetric(idx, 'value', e.target.value)}
                  placeholder="500 sqm"
                  className="w-full text-center text-lg font-bold text-[#c9a84c] border-0 focus:outline-none bg-transparent mb-1"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                />
                <input
                  type="text"
                  value={metric.label || ''}
                  onChange={(e) => updateMetric(idx, 'label', e.target.value)}
                  placeholder="Project Area"
                  className="w-full text-center text-xs text-[#6b7280] border-0 focus:outline-none bg-transparent uppercase tracking-wider"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      <div>
        <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">
          Summary
        </label>
        <textarea
          value={rs.summary || ''}
          onChange={(e) => update('summary', e.target.value)}
          placeholder={`${BRAND_NAME} brings together a highly experienced team...`}
          rows={4}
          className="w-full px-3 py-2 text-sm border border-[#e5e4df] rounded-md focus:outline-none focus:ring-1 focus:ring-[#c9a84c] focus:border-[#c9a84c] bg-white"
        />
      </div>
    </div>
  );
}
